import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "./Button";
import { ChatPanel } from "./chat";
import {
  DEFAULT_PLAYER_COLOR,
  GUEST_PLAYER_COLOR,
  DEFAULT_PLAYER_EMOJI,
  GUEST_PLAYER_EMOJI,
} from "../constants";
import {
  useWebSocketContext,
  ConnectionStatus,
} from "../contexts/WebSocketContext";
import { triggerActiveSessionRefresh } from "../hooks/useActiveSession";
import { useNavigationBlocker } from "../hooks/useNavigationBlocker";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useGameStore } from "../store/gameStore";
import { useSettingsStore } from "../store/settingsStore";
import { CharacterPicker } from "./games/terrible_psychiatrist/components/CharacterPicker";
import { AdvancedSettingsModal } from "./lobby/AdvancedSettingsModal";
import { LeaveConfirmModal } from "./lobby/LeaveConfirmModal";
import { LobbyToolbar } from "./lobby/LobbyToolbar";
import { NpcManager } from "./lobby/NpcManager";
import { PresetSelector } from "./lobby/PresetSelector";
import { SpectatorsSection } from "./lobby/SpectatorsSection";
import {
  ClientActionEnum,
  type LobbyState,
  type PlayerInfo,
} from "../types/game";
import { formatPlayerName } from "../utils/helpers";
import type { Character } from "../types/terriblePsychiatrist";

// Note: onGameStart callback removed - navigation is now handled by WebSocketContext
export function Lobby() {
  const {
    sessionId,
    roomCode,
    playerId,
    gameState,
    updateGameState,
    spectators,
    allowSpectators,
    updateLobbySettings,
  } = useGameStore();
  const lobbyState = gameState as LobbyState | null;
  const { sendMessage, status: wsStatus } = useWebSocketContext();
  const {
    currentSettings,
    configSchema,
    isAdvancedOpen,
    openAdvanced,
    closeAdvanced,
    applyPreset,
    fetchPresets,
    fetchSchema,
    setSettings,
  } = useSettingsStore();
  const navigate = useNavigate();

  // WebSocket is now managed by WebSocketContext - no local handling needed

  const [isStarting, setIsStarting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(() => {
    // Read from localStorage for global advanced mode
    // Wrapped in try-catch for private browsing mode compatibility
    try {
      return localStorage.getItem("advancedMode") === "true";
    } catch {
      return false;
    }
  });
  const [copied, setCopied] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [showNpcs, setShowNpcs] = useState(false);
  const [copiedClaimCode, setCopiedClaimCode] = useState<string | null>(null);

  // Block browser navigation (back button) and show confirmation
  useNavigationBlocker({
    when: !allowNavigation, // Block unless explicitly allowing navigation
    onBlock: () => {
      // Show the leave confirmation modal
      setShowLeaveConfirm(true);
      return false; // Don't proceed with navigation
    },
    message: "Are you sure you want to leave the lobby?",
  });

  // Keyboard shortcut: "^" (Shift+6) toggles advanced mode globally
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check for "^" key (Shift+6 or caret character)
      if (e.key === "^" || (e.shiftKey && e.key === "6")) {
        e.preventDefault();
        setAdvancedMode((prev) => {
          const newValue = !prev;
          try {
            localStorage.setItem("advancedMode", String(newValue));
          } catch {
            // localStorage unavailable (private browsing mode)
          }
          return newValue;
        });
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // Fetch presets and schema when game changes
  useEffect(() => {
    if (lobbyState?.game_id) {
      fetchPresets(lobbyState.game_id);
      fetchSchema(lobbyState.game_id);
    }
  }, [lobbyState?.game_id, fetchPresets, fetchSchema]);

  // Sync lobby settings from game state
  useEffect(() => {
    if (lobbyState?.lobby_settings && !currentSettings) {
      setSettings(lobbyState.lobby_settings);
    }
  }, [lobbyState?.lobby_settings, currentSettings, setSettings]);

  // Defensive check: if game has started (room_status changed from 'lobby'), navigate to game
  // This handles cases where the initial broadcast was missed or navigation didn't trigger
  useEffect(() => {
    if (!sessionId) return;

    const extendedState = lobbyState as unknown as { phase?: string };
    const roomStatus = lobbyState?.room_status || extendedState?.phase;
    const isGameStarted = roomStatus && roomStatus !== "lobby";

    if (isGameStarted) {
      logger.info("lobby_detected_game_started", {
        roomStatus,
        phase: extendedState?.phase,
        navigatingTo: `/game/${sessionId}`,
      });
      // Allow navigation to bypass the navigation blocker, then navigate
      setAllowNavigation(true);
      navigate(`/game/${sessionId}`);
    }
  }, [lobbyState?.room_status, sessionId, navigate]);

  // Format room code for display - handles both dash format and session IDs
  const getDisplayRoomCode = (): string => {
    if (!roomCode) {
      return "Loading...";
    }

    // Check if it looks like a magic room code (format: N-word-N-word-N-word)
    const parts = roomCode.split("-");
    if (parts.length === 6) {
      // It's a proper magic code like "8-hamsters-2-palettes-9-computers"
      return roomCode; // Display in dash format
    }

    // Otherwise it's a session ID or malformed - log warning
    logger.warn("lobby_invalid_room_code", { roomCode, sessionId });
    return roomCode; // Display as-is
  };

  // Generate the join link
  const getJoinLink = () => {
    if (!roomCode) return window.location.origin;
    return `${window.location.origin}/magic/${roomCode}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getJoinLink());
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error("clipboard_copy_failed", { error: err });
      toast.error("Failed to copy link");
    }
  };

  const handleLeaveRoom = async () => {
    setIsLeaving(true);
    setShowLeaveConfirm(false);
    // Allow navigation to proceed without blocking
    setAllowNavigation(true);

    try {
      // Notify server to remove player from session
      await api.leaveMySession();
      triggerActiveSessionRefresh();
      logger.info("room_left", { sessionId, playerId });
    } catch (error) {
      logger.error("leave_room_failed", { error, sessionId, playerId });
      // Continue with local cleanup even if server call fails
    }

    // Clear local state and navigate to home
    useGameStore.getState().clearSession();
    window.location.href = "/";
  };

  const handleStartGame = async () => {
    // Determine host status: use backend value, or fallback to first player in list
    const currentPlayers = lobbyState?.current_players ?? [];
    const isHost =
      lobbyState?.is_host ?? currentPlayers[0]?.player_id === playerId;

    if (!isHost) {
      toast.error("Only the host can start the game");
      return;
    }

    setIsStarting(true);
    try {
      sendMessage(ClientActionEnum.START_GAME);
    } catch (error) {
      logger.error("game_start_failed", { error });
      // Backend will send error message via WebSocket if requirements not met
      toast.error("Failed to start game. Check game requirements.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleTogglePrivacy = async () => {
    if (!lobbyState?.is_host) {
      toast.error("Only the host can change privacy settings");
      return;
    }

    const newPrivacy = !(lobbyState.is_public ?? true);
    try {
      // Update via API
      await api.toggleRoomPrivacy(sessionId!, newPrivacy);

      // Optimistically update local state
      updateGameState({
        ...lobbyState,
        is_public: newPrivacy,
      });

      toast.success(newPrivacy ? "Room is now public" : "Room is now private");
    } catch (error) {
      logger.error("privacy_toggle_failed", { error });
      toast.error("Failed to update privacy setting");
    }
  };

  const handleToggleSpectators = () => {
    if (!lobbyState?.is_host) {
      toast.error("Only the host can change spectator settings");
      return;
    }

    const newAllow = !allowSpectators;
    sendMessage("TOGGLE_SPECTATORS", { allow_spectators: newAllow });
    logger.info("spectators_toggled", { allow: newAllow });
  };

  const handleCopyClaimCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedClaimCode(code);
      toast.success("Claim code copied!");
      setTimeout(() => setCopiedClaimCode(null), 2000);
    } catch (err) {
      logger.error("claim_code_copy_failed", { error: err });
      toast.error("Failed to copy claim code");
    }
  };

  const handlePresetSelect = (presetName: string) => {
    if (!lobbyState?.is_host) {
      toast.error("Only the host can change game settings");
      return;
    }

    applyPreset(presetName);
    logger.info("preset_selected", { presetName });
    // MOCK: In production, send UPDATE_LOBBY_SETTINGS WebSocket message with preset settings
    toast.success(`Preset "${presetName}" applied`);
  };

  const handleSaveSettings = (settings: Record<string, unknown>) => {
    if (!lobbyState?.is_host) {
      toast.error("Only the host can change game settings");
      return;
    }

    // MOCK: In production, send UPDATE_LOBBY_SETTINGS WebSocket message
    logger.info("settings_saved", { settings });
    updateLobbySettings(settings as Parameters<typeof updateLobbySettings>[0]);
    toast.success("Settings saved");
  };

  // Character selection handler for games that require it (e.g., Terrible Psychiatrist)
  const [selectingCharacter, setSelectingCharacter] = useState(false);

  const handleSelectCharacter = useCallback(
    (characterId: string) => {
      if (selectingCharacter) return;
      setSelectingCharacter(true);
      logger.info("character_selected", { characterId });
      sendMessage(ClientActionEnum.SELECT_CHARACTER, {
        character_id: characterId,
      });
      // Reset after a short delay to allow re-selection if needed
      setTimeout(() => setSelectingCharacter(false), 500);
    },
    [sendMessage, selectingCharacter],
  );

  // Extract character state from game state (for games like Terrible Psychiatrist)
  const rawState = lobbyState as Record<string, unknown> | null;
  const characters: Character[] = (rawState?.characters as Character[]) || [];
  const selectedCharacterId: string | null =
    (rawState?.my_character_id as string) || null;
  const allCharactersSelected: boolean =
    (rawState?.all_characters_selected as boolean) || false;
  const hasCharacterSelection = characters.length > 0;

  // Format game name for display
  const getGameName = (): string => {
    if (!lobbyState?.game_id) return "Lobby";

    // Convert game_id to readable name (e.g., "terrible-psychiatrist" -> "Terrible Psychiatrist")
    return lobbyState.game_id
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!lobbyState) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading lobby...</p>
        </div>
      </div>
    );
  }

  const currentPlayers = lobbyState.current_players ?? [];
  // Determine host status: use backend value, or fallback to first player in list
  const isHost =
    lobbyState.is_host ?? currentPlayers[0]?.player_id === playerId;

  return (
    <div className="min-h-screen" data-testid="lobby-container">
      {/* Magic Code Banner - Copyable (same as GameBoard) */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-1 text-center px-2">
        <button
          onClick={handleCopyLink}
          className="font-mono text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wider text-black hover:text-gray-800 transition-colors cursor-pointer truncate max-w-full"
          title="Click to copy join link"
          data-testid="lobby-room-code"
        >
          {getDisplayRoomCode()}
        </button>
        {copied && (
          <span
            className="ml-2 text-black text-sm"
            data-testid="lobby-copy-success"
          >
            ✓ Copied!
          </span>
        )}
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="glass rounded-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h1
              className="font-display text-3xl font-bold"
              data-testid="lobby-header"
            >
              {getGameName()}
            </h1>
            <div
              className="flex items-center gap-2"
              data-testid="lobby-connection-status"
            >
              <div
                className={`h-3 w-3 rounded-full ${
                  wsStatus === ConnectionStatus.CONNECTED
                    ? "bg-green-500"
                    : wsStatus === ConnectionStatus.CONNECTING
                      ? "bg-yellow-500 animate-pulse"
                      : "bg-red-500"
                }`}
                data-testid="lobby-connection-indicator"
              />
              <span className="text-sm" data-testid="lobby-connection-text">
                {wsStatus === ConnectionStatus.CONNECTED
                  ? "Connected"
                  : wsStatus === ConnectionStatus.CONNECTING
                    ? "Connecting..."
                    : "Disconnected"}
              </span>
            </div>
          </div>

          {/* Lobby Toolbar - Privacy, Spectators, NPCs, Settings */}
          <LobbyToolbar
            isPublic={lobbyState?.is_public ?? true}
            allowSpectators={allowSpectators}
            spectatorCount={spectators.length}
            showNpcs={showNpcs}
            isHost={isHost}
            onTogglePublic={handleTogglePrivacy}
            onToggleSpectators={handleToggleSpectators}
            onToggleNpcs={() => setShowNpcs((prev) => !prev)}
            onOpenSettings={openAdvanced}
          />

          {/* Preset Selector - Host Only */}
          {isHost && currentSettings && (
            <PresetSelector
              gameId={lobbyState?.game_id || "unknown"}
              currentSettings={currentSettings}
              onPresetSelect={handlePresetSelect}
              disabled={!isHost}
            />
          )}

          {/* Spectators Section */}
          {spectators.length > 0 && (
            <SpectatorsSection spectators={spectators} />
          )}

          {/* Character Selection - For games that require it (e.g., Terrible Psychiatrist) */}
          {hasCharacterSelection && (
            <div className="mb-6">
              <CharacterPicker
                characters={characters}
                selectedCharacterId={selectedCharacterId}
                playerId={playerId!}
                allPlayers={currentPlayers}
                onSelectCharacter={handleSelectCharacter}
                disabled={selectingCharacter}
              />
              {!allCharactersSelected && (
                <p className="text-center text-sm text-yellow-400 mt-3">
                  All players must select a character before starting
                </p>
              )}
            </div>
          )}

          {/* Advanced Section - Visible only when advanced mode is active (press ^ to toggle) */}
          {advancedMode && (
            <div className="mb-6 text-xs text-gray-400">
              <span className="font-medium">Session ID:</span>{" "}
              <code className="bg-black/20 px-1 rounded">{sessionId}</code>
            </div>
          )}

          <div className="mb-8">
            <h2
              className="text-xl font-semibold mb-4"
              data-testid="lobby-player-count"
            >
              Players ({currentPlayers.length}/{lobbyState.max_players || "∞"})
            </h2>
            {lobbyState.min_players &&
              currentPlayers.length < lobbyState.min_players && (
                <p
                  className="text-sm text-yellow-400 mb-2"
                  data-testid="lobby-min-players-warning"
                >
                  Need at least {lobbyState.min_players} players to start
                </p>
              )}
            <div className="space-y-2" data-testid="lobby-player-list">
              {currentPlayers.map((player: PlayerInfo) => (
                <div
                  key={player.player_id}
                  className="flex items-center justify-between bg-white/5 rounded-lg p-3"
                  data-testid={`lobby-player-item-${player.player_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center font-bold text-white text-2xl relative"
                      style={{
                        backgroundColor: player.is_guest
                          ? GUEST_PLAYER_COLOR
                          : player.theme_primary_color || DEFAULT_PLAYER_COLOR,
                      }}
                    >
                      {player.profile_emoji ||
                        (player.is_guest
                          ? GUEST_PLAYER_EMOJI
                          : DEFAULT_PLAYER_EMOJI)}
                      {player.is_npc && (
                        <span
                          className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs px-1 rounded"
                          title="AI Player"
                        >
                          AI
                        </span>
                      )}
                    </div>
                    <div>
                      <p
                        className="font-medium"
                        data-testid={`lobby-player-name-${player.player_id}`}
                      >
                        {formatPlayerName(
                          player.display_name,
                          player.player_id === playerId,
                        )}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {player.player_id === currentPlayers[0]?.player_id && (
                          <span
                            className="text-xs text-yellow-400"
                            data-testid={`lobby-player-host-badge-${player.player_id}`}
                          >
                            Host
                          </span>
                        )}
                        {player.is_npc && (
                          <span
                            className="text-xs text-purple-400"
                            data-testid={`lobby-player-npc-badge-${player.player_id}`}
                          >
                            AI Player
                          </span>
                        )}
                        {/* Show claim code to host only */}
                        {isHost && player.is_npc && player.claim_code && (
                          <button
                            onClick={() =>
                              handleCopyClaimCode(player.claim_code!)
                            }
                            className="text-xs font-mono bg-purple-900/50 px-2 py-0.5 rounded hover:bg-purple-800/50 transition-colors"
                            title="Click to copy claim code"
                            data-testid={`lobby-claim-code-${player.player_id}`}
                          >
                            {copiedClaimCode === player.claim_code
                              ? "✓ Copied!"
                              : player.claim_code}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`h-2 w-2 rounded-full ${
                      player.is_connected ? "bg-green-500" : "bg-gray-500"
                    }`}
                    data-testid={`lobby-player-status-${player.player_id}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* NPC Manager - Host Only, visible when showNpcs is true */}
          {isHost && showNpcs && <NpcManager />}

          <div className="flex gap-4 mt-8">
            {isHost ? (
              <Button
                variant="success"
                onClick={handleStartGame}
                disabled={
                  isStarting ||
                  currentPlayers.length < (lobbyState.min_players ?? 2) ||
                  (hasCharacterSelection && !allCharactersSelected)
                }
                className="flex-1"
                data-testid="lobby-start-game-button"
              >
                {isStarting ? "Starting..." : "Start Game"}
              </Button>
            ) : (
              <div
                className="flex-1 text-center py-2 bg-white/5 rounded-lg"
                data-testid="lobby-waiting-message"
              >
                <p className="text-sm text-gray-300">
                  Waiting for host to start the game...
                </p>
              </div>
            )}

            <Button
              variant="secondary"
              onClick={() => setShowLeaveConfirm(true)}
              disabled={isLeaving}
              data-testid="lobby-leave-button"
            >
              {isLeaving ? "Leaving..." : "Leave"}
            </Button>
          </div>
        </div>
      </div>

      {/* Leave Confirmation Modal */}
      <LeaveConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleLeaveRoom}
      />

      {/* Advanced Settings Modal */}
      {currentSettings && configSchema && (
        <AdvancedSettingsModal
          isOpen={isAdvancedOpen}
          onClose={closeAdvanced}
          gameId={lobbyState?.game_id || "unknown"}
          currentSettings={currentSettings}
          configSchema={configSchema}
          currentPlayerCount={currentPlayers.length}
          onSave={handleSaveSettings}
        />
      )}

      {/* Chat Panel */}
      <ChatPanel sendMessage={sendMessage} />
    </div>
  );
}
